import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus, Trash2, Image as ImageIcon, Megaphone, BookOpen, Mail, Sparkles, Pin, Hash } from "lucide-react";

const SPONSOR_TIERS = ["title", "gold", "silver", "court", "partner"];

export default function CMS() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/site-content");
        setContent(data);
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  const save = async (section, payload) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-content", { [section]: payload });
      setContent(data);
      toast.success(`Saved ${section.replace("_", " ")}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  if (loading || !content) return <div className="p-10 text-zinc-400">Loading CMS…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Website Content Management</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white" data-testid="cms-title">Site Content</h1>
        <div className="text-xs text-zinc-400 mt-1">Edits go live immediately on the public home page. Last updated: {content.updated_at || "never"}</div>
      </div>

      <Tabs defaultValue="hero" data-testid="cms-tabs">
        <TabsList className="grid grid-cols-7 w-full bg-[#11241b] border border-white/10 h-auto p-1">
          <TabsTrigger value="hero" data-testid="cms-tab-hero" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]"><Sparkles className="w-3.5 h-3.5 mr-1.5"/>Hero</TabsTrigger>
          <TabsTrigger value="recap" data-testid="cms-tab-recap" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]"><ImageIcon className="w-3.5 h-3.5 mr-1.5"/>Recap</TabsTrigger>
          <TabsTrigger value="story" data-testid="cms-tab-story" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]"><BookOpen className="w-3.5 h-3.5 mr-1.5"/>Story</TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="cms-tab-sponsors" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]">Sponsors</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="cms-tab-gallery" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]">Gallery</TabsTrigger>
          <TabsTrigger value="social" data-testid="cms-tab-social" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]"><Hash className="w-3.5 h-3.5 mr-1.5"/>Social</TabsTrigger>
          <TabsTrigger value="news" data-testid="cms-tab-news" className="text-xs uppercase tracking-widest data-[state=active]:bg-[#060d09]"><Megaphone className="w-3.5 h-3.5 mr-1.5"/>News</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="mt-4"><HeroEditor value={content.hero} onSave={(v) => save("hero", v)} saving={saving} /></TabsContent>
        <TabsContent value="recap" className="mt-4"><RecapEditor value={content.season_recap} onSave={(v) => save("season_recap", v)} saving={saving} /></TabsContent>
        <TabsContent value="story" className="mt-4"><StoryEditor value={content.story_timeline || []} onSave={(v) => save("story_timeline", v)} saving={saving} /></TabsContent>
        <TabsContent value="sponsors" className="mt-4"><SponsorsEditor value={content.sponsors || []} onSave={(v) => save("sponsors", v)} saving={saving} /></TabsContent>
        <TabsContent value="gallery" className="mt-4"><GalleryEditor value={content.gallery || []} onSave={(v) => save("gallery", v)} saving={saving} /></TabsContent>
        <TabsContent value="social" className="mt-4"><HashtagWallEditor value={content.hashtag_wall || {}} onSave={(v) => save("hashtag_wall", v)} saving={saving} /></TabsContent>
        <TabsContent value="news" className="mt-4">
          <AnnouncementsEditor value={content.announcements || []} onSave={(v) => save("announcements", v)} saving={saving} />
          <div className="mt-6"><ContactEditor value={content.contact || {}} onSave={(v) => save("contact", v)} saving={saving} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================================================
   Section editors
   ============================================================ */
function HeroEditor({ value, onSave, saving }) {
  const [v, setV] = useState(value || {});
  const set = (k, x) => setV({ ...v, [k]: x });
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-4" data-testid="cms-hero-editor">
      <SectionTitle title="Hero Block" subtitle="Top of the public home page (/)" />
      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Kicker (small label above title)</Label>
        <Input data-testid="cms-hero-kicker" value={v.kicker || ""} onChange={(e) => set("kicker", e.target.value)} className="bg-[#060d09] border-white/10 text-white mt-1" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <FieldText label="Line 1 (white)" testid="cms-hero-line1" value={v.line1 || ""} onChange={(x) => set("line1", x)} />
        <FieldText label="Line 2 (red)"   testid="cms-hero-line2" value={v.line2 || ""} onChange={(x) => set("line2", x)} />
        <FieldText label="Line 3 (green)" testid="cms-hero-line3" value={v.line3 || ""} onChange={(x) => set("line3", x)} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Subtitle paragraph</Label>
        <Textarea data-testid="cms-hero-subtitle" rows={3} value={v.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="bg-[#060d09] border-white/10 text-white mt-1" />
      </div>
      <FieldText label="Background image URL" testid="cms-hero-bg" value={v.background_image || ""} onChange={(x) => set("background_image", x)} placeholder="/assets/hero-padel.jpg" />
      <SaveBar saving={saving} onSave={() => onSave(v)} testid="cms-hero-save" />
    </Card>
  );
}

function RecapEditor({ value, onSave, saving }) {
  const [v, setV] = useState(value || {});
  const set = (k, x) => setV({ ...v, [k]: x });
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-4" data-testid="cms-recap-editor">
      <SectionTitle title="Last Season Recap" subtitle="The framed photo + headline in the Story section" />
      <FieldText label="Small label (e.g. 'Season 02 · Recap')" testid="cms-recap-label" value={v.label || ""} onChange={(x) => set("label", x)} />
      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Headline</Label>
        <Textarea data-testid="cms-recap-title" rows={2} value={v.title || ""} onChange={(e) => set("title", e.target.value)} className="bg-[#060d09] border-white/10 text-white mt-1" />
      </div>
      <FieldText label="Image URL" testid="cms-recap-image" value={v.image || ""} onChange={(x) => set("image", x)} placeholder="/assets/season-2-action.jpg" />
      <FieldText label="Image ALT text"  testid="cms-recap-alt"   value={v.alt   || ""} onChange={(x) => set("alt",   x)} />
      <SaveBar saving={saving} onSave={() => onSave(v)} testid="cms-recap-save" />
    </Card>
  );
}

function StoryEditor({ value, onSave, saving }) {
  const [items, setItems] = useState(value);
  const update = (i, k, x) => { const next = [...items]; next[i] = { ...next[i], [k]: x }; setItems(next); };
  const add = () => setItems([...items, { year: `S0${items.length + 1}`, title: "", blurb: "" }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-3" data-testid="cms-story-editor">
      <SectionTitle title="Story Timeline" subtitle="Past seasons / milestones rendered in the Story section" />
      {items.map((it, i) => (
        <div key={i} className="grid md:grid-cols-[80px_1fr_2fr_auto] gap-2 items-start" data-testid={`cms-story-row-${i}`}>
          <Input value={it.year || ""} onChange={(e) => update(i, "year", e.target.value)} placeholder="S01" className="bg-[#060d09] border-white/10 text-white" />
          <Input value={it.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="Title" className="bg-[#060d09] border-white/10 text-white" />
          <Input value={it.blurb || ""} onChange={(e) => update(i, "blurb", e.target.value)} placeholder="Blurb" className="bg-[#060d09] border-white/10 text-white" />
          <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:bg-red-500/10" data-testid={`cms-story-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <Button size="sm" onClick={add} variant="outline" className="border-white/10 text-zinc-200" data-testid="cms-story-add"><Plus className="w-4 h-4 mr-1" /> Add Row</Button>
        <SaveBar saving={saving} onSave={() => onSave(items)} testid="cms-story-save" />
      </div>
    </Card>
  );
}

function SponsorsEditor({ value, onSave, saving }) {
  const [items, setItems] = useState(value);
  const update = (i, k, x) => { const next = [...items]; next[i] = { ...next[i], [k]: x }; setItems(next); };
  const add = () => setItems([...items, { name: "", logo_url: "", tier: "partner", website: "" }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-3" data-testid="cms-sponsors-editor">
      <SectionTitle title="Sponsors" subtitle="Logos rendered in the sponsor strip at the bottom of the home page" />
      {items.map((it, i) => (
        <div key={i} className="grid md:grid-cols-[1fr_2fr_120px_2fr_auto] gap-2 items-start" data-testid={`cms-sponsor-row-${i}`}>
          <Input value={it.name || ""} onChange={(e) => update(i, "name", e.target.value)} placeholder="Brand" className="bg-[#060d09] border-white/10 text-white" />
          <Input value={it.logo_url || ""} onChange={(e) => update(i, "logo_url", e.target.value)} placeholder="Logo URL" className="bg-[#060d09] border-white/10 text-white" />
          <Select value={it.tier || "partner"} onValueChange={(x) => update(i, "tier", x)}>
            <SelectTrigger className="bg-[#060d09] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#11241b] border-white/10 text-white">{SPONSOR_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={it.website || ""} onChange={(e) => update(i, "website", e.target.value)} placeholder="https://" className="bg-[#060d09] border-white/10 text-white" />
          <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:bg-red-500/10" data-testid={`cms-sponsor-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <Button size="sm" onClick={add} variant="outline" className="border-white/10 text-zinc-200" data-testid="cms-sponsor-add"><Plus className="w-4 h-4 mr-1" /> Add Sponsor</Button>
        <SaveBar saving={saving} onSave={() => onSave(items)} testid="cms-sponsors-save" />
      </div>
    </Card>
  );
}

function GalleryEditor({ value, onSave, saving }) {
  const [items, setItems] = useState(value);
  const update = (i, k, x) => { const next = [...items]; next[i] = { ...next[i], [k]: x }; setItems(next); };
  const add = () => setItems([...items, { image_url: "", caption: "" }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-3" data-testid="cms-gallery-editor">
      <SectionTitle title="Gallery" subtitle="Image grid that appears between Players and Sponsors on the home page" />
      {items.map((it, i) => (
        <div key={i} className="grid md:grid-cols-[100px_3fr_2fr_auto] gap-2 items-start" data-testid={`cms-gallery-row-${i}`}>
          {it.image_url ? <img src={it.image_url} alt="" className="h-16 w-full object-cover rounded border border-white/10" /> : <div className="h-16 w-full rounded border border-dashed border-white/10 flex items-center justify-center text-[10px] text-zinc-600">no preview</div>}
          <Input value={it.image_url || ""} onChange={(e) => update(i, "image_url", e.target.value)} placeholder="Image URL" className="bg-[#060d09] border-white/10 text-white" />
          <Input value={it.caption || ""} onChange={(e) => update(i, "caption", e.target.value)} placeholder="Caption" className="bg-[#060d09] border-white/10 text-white" />
          <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:bg-red-500/10" data-testid={`cms-gallery-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <Button size="sm" onClick={add} variant="outline" className="border-white/10 text-zinc-200" data-testid="cms-gallery-add"><Plus className="w-4 h-4 mr-1" /> Add Image</Button>
        <SaveBar saving={saving} onSave={() => onSave(items)} testid="cms-gallery-save" />
      </div>
    </Card>
  );
}

function AnnouncementsEditor({ value, onSave, saving }) {
  const [items, setItems] = useState(value);
  const update = (i, k, x) => { const next = [...items]; next[i] = { ...next[i], [k]: x }; setItems(next); };
  const add = () => setItems([...items, { title: "", body: "", published_at: new Date().toISOString().slice(0, 10), pinned: false }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-3" data-testid="cms-news-editor">
      <SectionTitle title="Announcements & News" subtitle="Pinned/dated notices rendered above the Gallery section" />
      {items.map((it, i) => (
        <div key={i} className="rounded-lg p-3 bg-[#060d09] border border-white/10 space-y-2" data-testid={`cms-news-row-${i}`}>
          <div className="grid md:grid-cols-[2fr_1fr_auto] gap-2">
            <Input value={it.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="Title" className="bg-[#11241b] border-white/10 text-white" />
            <Input type="date" value={it.published_at || ""} onChange={(e) => update(i, "published_at", e.target.value)} className="bg-[#11241b] border-white/10 text-white" />
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300">
              <input type="checkbox" checked={!!it.pinned} onChange={(e) => update(i, "pinned", e.target.checked)} />
              <Pin className="w-3.5 h-3.5" /> Pinned
            </label>
          </div>
          <Textarea rows={3} value={it.body || ""} onChange={(e) => update(i, "body", e.target.value)} placeholder="Body" className="bg-[#11241b] border-white/10 text-white" />
          <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:bg-red-500/10" data-testid={`cms-news-remove-${i}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button></div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <Button size="sm" onClick={add} variant="outline" className="border-white/10 text-zinc-200" data-testid="cms-news-add"><Plus className="w-4 h-4 mr-1" /> Add Announcement</Button>
        <SaveBar saving={saving} onSave={() => onSave(items)} testid="cms-news-save" />
      </div>
    </Card>
  );
}

function HashtagWallEditor({ value, onSave, saving }) {
  const [v, setV] = useState(value || {});
  const [posts, setPosts] = useState(value?.posts || []);
  const set = (k, x) => setV({ ...v, [k]: x });
  const update = (i, k, x) => { const next = [...posts]; next[i] = { ...next[i], [k]: x }; setPosts(next); };
  const add = () => setPosts([...posts, { author: "", handle: "", avatar_url: "", image_url: "", caption: "", permalink: "" }]);
  const remove = (i) => setPosts(posts.filter((_, idx) => idx !== i));
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-4" data-testid="cms-social-editor">
      <SectionTitle title="Social / Hashtag Wall" subtitle="Curated posts shown on the public /live page. Paste image + caption from Instagram/X." />
      <div className="grid md:grid-cols-[120px_1fr_1fr] gap-3 items-end">
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300">
          <input type="checkbox" data-testid="cms-social-enabled" checked={v.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} /> Enabled
        </label>
        <FieldText label="Hashtag" testid="cms-social-hashtag" value={v.hashtag || ""} onChange={(x) => set("hashtag", x)} placeholder="#MaraPadelS3" />
        <FieldText label="Heading" testid="cms-social-heading" value={v.heading || ""} onChange={(x) => set("heading", x)} placeholder="Join The Conversation" />
      </div>
      <FieldText label="Subheading" testid="cms-social-subheading" value={v.subheading || ""} onChange={(x) => set("subheading", x)} />

      <div className="space-y-3 pt-2">
        {posts.map((it, i) => (
          <div key={i} className="rounded-lg p-3 bg-[#060d09] border border-white/10 space-y-2" data-testid={`cms-social-row-${i}`}>
            <div className="grid md:grid-cols-2 gap-2">
              <Input value={it.author || ""} onChange={(e) => update(i, "author", e.target.value)} placeholder="Author name" className="bg-[#11241b] border-white/10 text-white" />
              <Input value={it.handle || ""} onChange={(e) => update(i, "handle", e.target.value)} placeholder="@handle" className="bg-[#11241b] border-white/10 text-white" />
            </div>
            <Input value={it.image_url || ""} onChange={(e) => update(i, "image_url", e.target.value)} placeholder="Image URL" className="bg-[#11241b] border-white/10 text-white" />
            <Input value={it.permalink || ""} onChange={(e) => update(i, "permalink", e.target.value)} placeholder="Post permalink (https://instagram.com/p/...)" className="bg-[#11241b] border-white/10 text-white" />
            <Textarea rows={2} value={it.caption || ""} onChange={(e) => update(i, "caption", e.target.value)} placeholder="Caption" className="bg-[#11241b] border-white/10 text-white" />
            <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-400 hover:bg-red-500/10" data-testid={`cms-social-remove-${i}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button></div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-2">
        <Button size="sm" onClick={add} variant="outline" className="border-white/10 text-zinc-200" data-testid="cms-social-add"><Plus className="w-4 h-4 mr-1" /> Add Post</Button>
        <SaveBar saving={saving} onSave={() => onSave({ ...v, posts })} testid="cms-social-save" />
      </div>
    </Card>
  );
}

function ContactEditor({ value, onSave, saving }) {
  const [v, setV] = useState(value || {});
  const set = (k, x) => setV({ ...v, [k]: x });
  return (
    <Card className="p-6 bg-[#11241b] border-white/10 space-y-4" data-testid="cms-contact-editor">
      <SectionTitle title="Contact Emails (footer)" subtitle="Rendered in the public footer's Contact column" />
      <div className="grid md:grid-cols-3 gap-3">
        <FieldText label="Owners email"   testid="cms-contact-owners"   value={v.owners_email   || ""} onChange={(x) => set("owners_email", x)} />
        <FieldText label="Partners email" testid="cms-contact-partners" value={v.partners_email || ""} onChange={(x) => set("partners_email", x)} />
        <FieldText label="Hello email"    testid="cms-contact-hello"    value={v.hello_email    || ""} onChange={(x) => set("hello_email", x)} />
      </div>
      <SaveBar saving={saving} onSave={() => onSave(v)} testid="cms-contact-save" />
    </Card>
  );
}

/* ============================================================
   Tiny helpers
   ============================================================ */
function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="font-anton text-2xl uppercase tracking-tight text-white">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>
    </div>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-400">{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bg-[#060d09] border-white/10 text-white mt-1" />
    </div>
  );
}

function SaveBar({ saving, onSave, testid }) {
  return (
    <Button data-testid={testid} disabled={saving} onClick={onSave} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
      <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Section"}
    </Button>
  );
}
